/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Component, EventEmitter, Inject, Input, OnDestroy, Output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';

import {deferSignalStateWrite} from '../util/template-state';

@Component({
  imports: [CommonModule],
  selector: 'adn-copy-to-clipboard-button',
  template: `
    <button type="button" [class]="buttonClass" (click)="copyText()">
      <span class="fa" [ngClass]="copied ? 'fa-check' : iconClass"></span><span *ngIf="displayLabel"> {{ displayLabel }}</span>
    </button>
  `
})
export class CopyToClipboardButtonComponent implements OnDestroy {
  @Input() text = '';
  @Input() label = 'Copy';
  @Input() copiedLabel = 'Copied';
  @Input() iconClass = 'fa-clipboard';
  @Input() buttonClass = 'btn btn-default btn-xs';
  @Output() copiedChange = new EventEmitter<boolean>();

  private readonly copiedState = signal(false);
  get copied(): boolean { return this.copiedState(); }
  set copied(value: boolean) { this.copiedState.set(value); }
  private copiedTimer: any = null;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
  }

  ngOnDestroy(): void {
    clearTimeout(this.copiedTimer);
  }

  get displayLabel(): string {
    if (!this.copied) {
      return this.label;
    }
    return this.copiedLabel || (this.label ? 'Copied' : '');
  }

  async copyText(): Promise<void> {
    const copied = await this.writeText(this.text || '');
    if (!copied) {
      return;
    }

    // Clipboard settles as a microtask during the click's zoneless tick.
    // Flipping `copied` then races checkNoChanges (NG0100: fa-copy → fa-check).
    deferSignalStateWrite(() => {
      this.copied = true;
      this.copiedChange.emit(true);
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        deferSignalStateWrite(() => {
          this.copied = false;
          this.copiedChange.emit(false);
        });
      }, 1500);
    });
  }

  private async writeText(text: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_error) {
      // Fall through to the execCommand fallback for older browsers.
    }

    const textArea = this.document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', 'readonly');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '-9999px';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    this.document.body.appendChild(textArea);
    const activeElement = this.document.activeElement as HTMLElement | null;
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);

    let copied = false;
    try {
      copied = this.document.execCommand('copy');
    } catch (_error) {
      copied = false;
    }

    if (activeElement?.focus) {
      activeElement.focus();
    }
    this.document.body.removeChild(textArea);
    return copied;
  }
}
